<template>
  <div class="eb-page">
    <div v-if="loaded === true">
      <!-- ── Hero Header ───────────────────────────────────────── -->
      <div class="eb-hero">
        <div class="eb-hero__breadcrumb">
          <b-link to="/buildings" class="eb-hero__crumb">
            <feather-icon icon="ChevronLeftIcon" size="13"/>
            <span class="ml-25">{{ msg('Buildings') }}</span>
          </b-link>
          <span class="eb-hero__crumb-sep">/</span>
          <span class="eb-hero__crumb-current">{{ msg('Edit Building') }}</span>
        </div>
        <div class="eb-hero__main">
          <div class="eb-hero__icon-wrap">
            <feather-icon icon="HomeIcon" size="22"/>
          </div>
          <div class="eb-hero__titles">
            <h1 class="eb-hero__title">{{ building.company || building.address || msg('Building') }}</h1>
            <p class="eb-hero__subtitle">
              <feather-icon icon="MapPinIcon" size="13" class="mr-25"/>
              <span v-if="building.address">{{ building.address }}</span>
              <span v-if="building.zip">, {{ building.zip }}</span>
              <span v-if="building.location">, {{ building.location }}</span>
              <span v-if="!building.address && !building.zip && !building.location" class="text-muted">{{ msg('No address yet') }}</span>
            </p>
          </div>
          <div class="eb-hero__meta">
            <b-badge v-if="building.status" pill variant="light-warning" class="eb-hero__status">
              <feather-icon icon="ActivityIcon" size="11" class="mr-25"/>{{ String(building.status).replaceAll('_', ' ') }}
            </b-badge>
            <span v-if="building.propertyIds && building.propertyIds.length" class="eb-hero__chip">
              <feather-icon icon="LayersIcon" size="13" class="mr-25"/>{{ building.propertyIds.length }} {{ msg('Real estates') }}
            </span>
          </div>
        </div>
        <b-alert v-if="building.buildingExpenses == null || building.buildingExpenses.length < 1" show variant="warning" class="eb-hero__alert mb-0 mt-1">
          <feather-icon icon="AlertTriangleIcon" size="15" class="mr-50"/>
          {{ msg('No expenses have been added to this building yet — your numbers are not correct.') }}
        </b-alert>
      </div>

      <!-- ── Tabs ──────────────────────────────────────────────── -->
      <b-tabs class="eb-tabs" content-class="eb-tabs__content" nav-wrapper-class="eb-tabs__nav-wrap" nav-class="eb-tabs__nav">

        <!-- Management Details Tab -->
        <b-tab active>
          <template #title>
            <div class="eb-tab-label"><feather-icon icon="PackageIcon" size="14"/><span>{{ msg('Management') }}</span></div>
          </template>

          <!-- Building details -->
          <section class="eb-section">
            <header class="eb-section__head">
              <div class="eb-section__title">
                <span class="eb-section__icon"><feather-icon icon="PackageIcon" size="16"/></span>
                <h4>{{ msg('Building details') }}</h4>
              </div>
            </header>
            <div class="eb-section__body">
              <building-management v-model="building" @change="() => buildingUpdated = true"/>
            </div>
          </section>

          <!-- Real estates -->
          <section class="eb-section">
            <header class="eb-section__head">
              <div class="eb-section__title">
                <span class="eb-section__icon"><feather-icon icon="UsersIcon" size="16"/></span>
                <h4>{{ msg('Real estates') }}</h4>
                <b-badge v-if="building.propertyIds && building.propertyIds.length" pill variant="light-primary" class="ml-50">
                  {{ building.propertyIds.length }}
                </b-badge>
              </div>
              <div class="eb-section__actions eb-add-property">
                <div class="eb-add-property__picker">
                  <real-estate-picker-for-building noBuildingYet :buildingId="building.id" v-model="newPropertyId"/>
                </div>
                <button :disabled="newPropertyId == null" id="addProperty" @click="addPropertyToBuilding"
                        class="btn btn-primary btn-sm eb-add-property__btn">
                  <feather-icon icon="UserPlusIcon" size="13" class="mr-25"/>
                  {{ msg('Add Property') }}
                </button>
              </div>
            </header>
            <div class="eb-section__body p-0">
              <building-properties @delete="onDelete" :historyMaxHeight="500" v-bind:value="building"/>
            </div>
          </section>

          <!-- Pictures -->
          <section class="eb-section">
            <header class="eb-section__head">
              <div class="eb-section__title">
                <span class="eb-section__icon"><i class="far fa-images"></i></span>
                <h4>{{ msg('Pictures') }}</h4>
              </div>
              <div class="eb-section__actions">
                <b-button variant="outline-primary" class="btn-icon eb-icon-btn" id="realEstate-upload-image" v-b-tooltip :title="msg('Upload')">
                  <feather-icon icon="UploadIcon"/>
                </b-button>
                <b-popover target="realEstate-upload-image" size="xl" placement="bottom">
                  <vue-dropzone id="video-dropzone"
                                :options="dropzoneImagesOptions"
                                :useCustomSlot="true"
                                :duplicateCheck="true"
                                @vdropzone-complete="uploadImageComplete"
                                @vdropzone-success="uploadImageSuccess">
                    <div class="dropzone-custom-content">
                      <h3 class="dropzone-custom-title">Drag and drop to upload content!</h3>
                      <div class="subtitle">...or click to select a file from your computer</div>
                    </div>
                  </vue-dropzone>
                </b-popover>
              </div>
            </header>
            <div class="eb-section__body">
              <realEstate-image-swiper v-model="building.attachments" @change="buildingUpdated = true"/>
            </div>
          </section>

          <!-- Features -->
          <section class="eb-section">
            <header class="eb-section__head">
              <div class="eb-section__title">
                <span class="eb-section__icon"><i class="fa-solid fa-bell-concierge"></i></span>
                <h4>{{ msg('Features') }}</h4>
              </div>
              <div class="eb-section__actions">
                <b-button variant="outline-primary" class="btn-icon eb-icon-btn" v-b-tooltip :title="msg('Add feature')" @click="()=>$refs.realEstateFeature.addNewFeature()">
                  <feather-icon icon="PlusIcon"/>
                </b-button>
              </div>
            </header>
            <div class="eb-section__body">
              <real-estate-feature ref="realEstateFeature" v-model="building.features" v-bind:buid="building.buid"/>
            </div>
          </section>

          <!-- Files -->
          <section class="eb-section">
            <header class="eb-section__head">
              <div class="eb-section__title">
                <span class="eb-section__icon"><feather-icon icon="FileIcon" size="16"/></span>
                <h4>{{ msg('Files') }}</h4>
              </div>
              <div class="eb-section__actions">
                <b-button size="sm" :variant="uploadFile ? 'warning' : 'outline-warning'" @click="() => uploadFile = !uploadFile">
                  <feather-icon icon="UploadIcon" size="13" class="mr-25"/>{{ msg("Upload file") }}
                </b-button>
              </div>
            </header>
            <div class="eb-section__body">
              <b-alert show variant="secondary" class="eb-files-alert">
                <feather-icon icon="AlertCircleIcon" size="14" class="mr-50 text-danger"/>
                <strong>{{ msg('Mandatory documents') }}:</strong>
                <span class="ml-50">Kaufvertrag · Finanzierung · Bauakte · Hausverwaltung · Versicherung · Fotos · Marketing · Grundrissplan · Gutachten · Kreditvertrag · Bauvorhaben · Sonstiges</span>
              </b-alert>

              <b-form-group
                  :label="msg('Enter here the email address and choose the documents down below (make sure you havent selected more than 25MB)')"
                  label-for="email" class="mt-1">
                <b-input-group>
                  <b-form-input id="email" v-model="emailAddress" :placeholder="msg('Email address')"/>
                  <b-input-group-append>
                    <b-button
                        :disabled="emailAddress == null || emailAddress.length === 0 || selectedFileIds.length < 1"
                        variant="outline-primary" v-b-tooltip :title="msg('Send Email')"
                        @click="sendAllFilesByMail">
                      <feather-icon icon="MailIcon" size="14" class="mr-25"/>EMAIL
                    </b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>

              <div class="files-data mt-1">
                <div class="upload-backdrop eb-dropzone-wrap" v-show="uploadFile === true">
                  <vue-dropzone id="contract-upload" class="h-100" :options="dropzoneOptions" :useCustomSlot="true"
                                :duplicateCheck="true" @vdropzone-complete="uploadComplete"
                                @vdropzone-success="uploadSuccess">
                    <div class="dropzone-custom-content">
                      <h3 class="dropzone-custom-title">Drag and drop to upload content!</h3>
                      <div class="subtitle">...or click to select a file from your computer</div>
                    </div>
                  </vue-dropzone>
                </div>

                <div class="eb-files-table">
                  <b-table-simple class="mb-0" responsive borderless>
                    <b-thead>
                      <b-tr class="eb-files-thead">
                        <b-th class="text-center" style="width: 8%">{{ msg('Select') }}</b-th>
                        <b-th style="width: 40%">{{ msg('File') }}</b-th>
                        <b-th class="text-center" style="width: 20%">{{ msg('Tag') }}</b-th>
                        <b-th class="text-center" style="width: 20%">{{ msg('Note') }}</b-th>
                        <b-th class="text-center" style="width: 12%">{{ msg('Operations') }}</b-th>
                      </b-tr>
                    </b-thead>
                  </b-table-simple>
                  <perfect-scrollbar class="position-relative" style="height: auto">
                    <b-table-simple class="mb-0" responsive borderless>
                      <b-tbody>
                        <b-tr v-for="(file, index) in (building.files || [])" v-bind:key="file.id" v-show="file.active" class="eb-files-row">
                          <b-td class="text-center" style="width: 8%">
                            <b-form-checkbox v-model="selectedFileIds" :value="file.id"/>
                          </b-td>
                          <b-td style="width: 40%">
                            <div class="d-flex align-items-center">
                              <i v-bind:class="iconByExt(file.filename)" class="eb-file-icon mr-1"></i>
                              <div>
                                <div class="eb-file-name">{{ file.filename }}</div>
                                <small v-if="file.size != null" class="text-muted">{{ getFileSize(file.size) }}</small>
                              </div>
                            </div>
                          </b-td>
                          <b-td class="text-center" style="width: 20%">
                            <v-select :options="fileTags" :reduce="(tag) => tag.value" label="title" v-model="file.tag"
                                      append-to-body @input="() => submitDocumentFile(index)">
                              <template v-slot:option="option">
                                <i class="d-inline-block text-left" style="width: 30px" :class="option.icon"></i>
                                <span>{{ option.title }}</span>
                              </template>
                            </v-select>
                          </b-td>
                          <b-td class="text-center" style="width: 20%">
                            <b-form-textarea v-model="file.notes" @change="() => submitDocumentFile(index)" size="sm" rows="1"></b-form-textarea>
                          </b-td>
                          <b-td class="text-center" style="width: 12%">
                            <b-link class="mr-1 eb-action-link" v-b-tooltip :title="msg('Download')" @click="() => onDownloadFile(file.id)">
                              <feather-icon icon="DownloadIcon" size="16"/>
                            </b-link>
                            <b-link class="eb-action-link" v-b-tooltip :title="msg('Preview')" @click="() => onPreviewFile(file.id)">
                              <feather-icon icon="EyeIcon" size="16"/>
                            </b-link>
                          </b-td>
                        </b-tr>
                      </b-tbody>
                    </b-table-simple>
                  </perfect-scrollbar>
                  <div v-if="!(building.files && building.files.some(f => f.active))" class="eb-empty">
                    <feather-icon icon="FileIcon" size="22" class="mb-25"/>
                    <div>{{ msg('No files uploaded yet') }}</div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </b-tab>

        <!-- Expenses -->
        <b-tab v-if="building.propertyIds != null && building.propertyIds.length > 0">
          <template #title>
            <div class="eb-tab-label"><feather-icon icon="CreditCardIcon" size="14"/><span>{{ msg('Expenses') }}</span></div>
          </template>
          <section class="eb-section">
            <header class="eb-section__head">
              <div class="eb-section__title">
                <span class="eb-section__icon"><feather-icon icon="CreditCardIcon" size="16"/></span>
                <h4>{{ msg('Expenses') }}</h4>
              </div>
            </header>
            <div class="eb-section__body p-0">
              <building-expenses-table :value="building"/>
            </div>
          </section>
        </b-tab>

        <!-- Invoices -->
        <b-tab>
          <template #title>
            <div class="eb-tab-label"><feather-icon icon="FileTextIcon" size="14"/><span>{{ msg('Invoices') }}</span></div>
          </template>
          <section class="eb-section">
            <header class="eb-section__head">
              <div class="eb-section__title">
                <span class="eb-section__icon"><feather-icon icon="FileTextIcon" size="16"/></span>
                <h4>{{ msg('Invoices') }}</h4>
              </div>
            </header>
            <div class="eb-section__body p-0">
              <building-invoice-table :value="building" @invoice="loadBuilding"/>
            </div>
          </section>
          <section class="eb-section">
            <header class="eb-section__head">
              <div class="eb-section__title">
                <span class="eb-section__icon"><feather-icon icon="RepeatIcon" size="16"/></span>
                <h4>{{ msg('Internal payments') }}</h4>
              </div>
            </header>
            <div class="eb-section__body p-0">
              <building-internal-payment-table :value="building" @invoice="loadBuilding"/>
            </div>
          </section>
        </b-tab>

        <!-- Description -->
        <b-tab>
          <template #title>
            <div class="eb-tab-label"><feather-icon icon="AlignLeftIcon" size="14"/><span>{{ msg('Description') }}</span></div>
          </template>
          <section class="eb-section">
            <header class="eb-section__head">
              <div class="eb-section__title">
                <span class="eb-section__icon"><feather-icon icon="AlignLeftIcon" size="16"/></span>
                <h4>{{ msg('Description') }}</h4>
              </div>
            </header>
            <div class="eb-section__body">
              <building-text v-model="building" @change="()=>buildingUpdated=true"/>
            </div>
          </section>
        </b-tab>

        <!-- Customers -->
        <b-tab>
          <template #title>
            <div class="eb-tab-label"><feather-icon icon="UsersIcon" size="14"/><span>{{ msg('Regular customers') }}</span></div>
          </template>
          <section class="eb-section">
            <header class="eb-section__head">
              <div class="eb-section__title">
                <span class="eb-section__icon"><feather-icon icon="UsersIcon" size="16"/></span>
                <h4>{{ msg('Regular customers') }}</h4>
                <small class="text-muted ml-50">({{ msg('>2 contracts') }})</small>
              </div>
            </header>
            <div class="eb-section__body p-0">
              <client-table :filter="clientFilter" @clientSelect="onClientSelect" v-bind:amountPerPage="50" :deleteStatus="0"/>
            </div>
          </section>
        </b-tab>

        <!-- Damages -->
        <b-tab>
          <template #title>
            <div class="eb-tab-label"><feather-icon icon="AlertTriangleIcon" size="14"/><span>{{ msg('Damages') }}</span></div>
          </template>
          <section class="eb-section">
            <header class="eb-section__head">
              <div class="eb-section__title">
                <span class="eb-section__icon"><feather-icon icon="AlertTriangleIcon" size="16"/></span>
                <h4>{{ msg('Damages') }}</h4>
              </div>
              <div class="eb-section__actions">
                <b-button variant="outline-primary" class="btn-icon eb-icon-btn" v-b-tooltip :title="msg('Refresh')" @click="$refs.damage.refresh()">
                  <feather-icon icon="RefreshCcwIcon" size="14"/>
                </b-button>
              </div>
            </header>
            <div class="eb-section__body">
              <damage-panel ref="damage" :buildingId="building.id"/>
            </div>
          </section>
        </b-tab>

        <!-- Todo -->
        <b-tab>
          <template #title>
            <div class="eb-tab-label"><feather-icon icon="CheckSquareIcon" size="14"/><span>{{ msg('Todo List') }}</span></div>
          </template>
          <section class="eb-section">
            <header class="eb-section__head">
              <div class="eb-section__title">
                <span class="eb-section__icon"><feather-icon icon="CheckSquareIcon" size="16"/></span>
                <h4>{{ msg('Todo List') }}</h4>
              </div>
            </header>
            <div class="eb-section__body">
              <building-todo v-model="building"/>
            </div>
          </section>
        </b-tab>

        <!-- Tracker History -->
        <b-tab>
          <template #title>
            <div class="eb-tab-label"><feather-icon icon="ClockIcon" size="14"/><span>{{ msg('Tracker') }}</span></div>
          </template>
          <section class="eb-section">
            <header class="eb-section__head">
              <div class="eb-section__title">
                <span class="eb-section__icon"><feather-icon icon="ClockIcon" size="16"/></span>
                <h4>{{ msg('History') }} / {{ msg('Status') }}</h4>
              </div>
            </header>
            <div class="eb-section__body">
              <div class="eb-tracker-row">
                <div class="eb-tracker-row__field">
                  <label class="text-muted small mb-25 d-block">{{ msg("Status") }}</label>
                  <v-select style="min-width: 280px" :dir="isRTL ? 'rtl' : 'ltr'"
                            :clearable="false" v-model="building.status" label="text" :options="allStatus"
                            :reduce="(status) => status.value" @option:selected="buildingUpdated = true"
                            class="w-100"/>
                </div>
                <b-button v-b-toggle.collapse-1 variant="outline-primary" class="eb-tracker-row__toggle">
                  <feather-icon icon="ListIcon" size="14" class="mr-50"/>{{ msg("Status history timeline") }}
                </b-button>
              </div>
              <b-collapse id="collapse-1" class="mt-1">
                <div v-if="building.histories != null && building.histories.length > 0" class="eb-timeline-wrap">
                  <app-timeline v-for="(statusHistory, index) in building.histories" v-bind:key="index">
                    <app-timeline-item class="mb-2"
                                       :title="statusHistory.status != null ? statusHistory.status.replace('_', ' ') : null"
                                       :subtitle="'Source: ' + statusHistory.source + (statusHistory.userId > 0 ? (', User: ' + getUserFullName(statusHistory.userId)) : '')"
                                       :time="statusHistory.time | moment('DD/MM/YY HH:mm')" :variant="null"
                                       :tracker-type="statusHistory.trackerType.replaceAll('_', ' ')"
                                       :tracker-value="statusHistory.value"/>
                  </app-timeline>
                </div>
                <div v-else class="eb-empty"><feather-icon icon="ClockIcon" size="22" class="mb-25"/><div>{{ msg('No history yet') }}</div></div>
              </b-collapse>
            </div>
          </section>
        </b-tab>

      </b-tabs>

      <!-- Floating save bar -->
      <transition name="eb-fade-up">
        <div v-if="buildingUpdated" class="eb-save-bar">
          <div class="eb-save-bar__inner">
            <span class="eb-save-bar__hint">
              <feather-icon icon="EditIcon" size="14" class="mr-50"/>{{ msg('Unsaved changes') }}
            </span>
            <b-button variant="outline-secondary" class="mr-50" @click="loadBuilding">
              <feather-icon icon="XIcon" size="14" class="mr-25"/>{{ msg("Cancel") }}
            </b-button>
            <b-button variant="primary" @click="updateBuilding">
              <feather-icon icon="CheckIcon" size="14" class="mr-25"/>{{ msg("Save") }}
            </b-button>
          </div>
        </div>
      </transition>
    </div>

    <div v-else class="eb-loading">
      <b-spinner variant="primary"/>
      <strong class="mt-1">{{ msg('Loading') }}…</strong>
    </div>

    <b-modal :id="infoModal.id" :title="msg('Confirmation')" @ok="createRefinance"
             ok-variant="success" @hide="resetInfoModal" :cancel-title="msg('cancel')">
      <p class="my-4">{{ msg('refinancequestion') }}</p>
    </b-modal>
  </div>

</template>

<script>

import {mapActions, mapGetters, mapState} from 'vuex'
import BuildingManagement from '@/views/realEstate/building/buildingManagement'
import StatisticCardHorizontal from '@core/components/statistics-cards/StatisticCardHorizontal.vue'
import clientTable from "@/components/components/clientTable";
import bankdetails from "@/components/panels/bank/bankdetails";
import vue2Dropzone from "vue2-dropzone";
import ClientPicker from "@/components/components/picker/clientPicker";
import buildingProperties from "@/components/panels/realEstates/buildingProperties";
import RealEstateImageSwiper from "@/components/panels/realEstates/realEstateImageSwiper";
import AppTimeline from "@core/components/app-timeline/AppTimeline";
import AppTimelineItem from "@core/components/app-timeline/AppTimelineItem";
import BuildingUserManagement from "@/views/realEstate/building/buildingUserManagement";
import buildingExpensesTable from "@/views/realEstate/building/buildingExpensesTable";
import BuildingInvoiceTable from "@/views/realEstate/building/BuildingInvoiceTable.vue";
import {BuildingModel} from "@/model/RealEstate/BuildingModel";
import BuildingText from "@/views/realEstate/building/buildingTexts";
import BuildingInternalPaymentTable from "@/views/realEstate/building/BuildingInternalPaymentTable.vue";
import DamagePanel from "@/components/panels/damages/DamagePanel.vue";
import BuildingTodo from "@/components/panels/realEstates/buildingTodo.vue";
import RealEstateFeature from "@/components/panels/realEstates/realEstateFeature.vue";
import RealEstatePickerForBuilding from "@/components/components/picker/realEstatesPickerForBuilding.vue";

export default {
  name: 'editBuilding',
  components: {
    RealEstatePickerForBuilding,
    RealEstateFeature,
    BuildingTodo,
    DamagePanel,
    BuildingText,
    BuildingInvoiceTable,
    BuildingInternalPaymentTable,
    clientTable,
    // eslint-disable-next-line vue/no-unused-components
    bankdetails,
    AppTimeline,
    AppTimelineItem,
    BuildingManagement,
    // eslint-disable-next-line vue/no-unused-components
    BuildingUserManagement,
    // eslint-disable-next-line vue/no-unused-components
    StatisticCardHorizontal,
    vueDropzone: vue2Dropzone,
    RealEstateImageSwiper,
    // eslint-disable-next-line vue/no-unused-components
    ClientPicker,
    buildingProperties,
    buildingExpensesTable
  },

  data() {
    return {
      testFile: null,
      loaded: false,
      error: null,
      infoModal: {
        id: 'info-modal',
        value: null,
        title: '',
        content: ''
      },
      building: {
        name: '',

      },
      emailAddress: null,
      selectedFileIds: [],
      newClientId: null,
      newPropertyId: null,
      clientFilter: {
        creationDateFrom: null,
        creationDateUtil: null,
        assignationDateFrom: null,
        assignationDateUtil: null,
        active: 1,
        ids: []
      },
      statusVariants:
          {
            "ACTIVE": "success", "PENDING": "warning", "SHIPPED": "success",
            "PAID": "success", "WAITING_FOR_PAYMENT": "warning", "PAYMENT_FAILED": "danger",
            "CANCELLED": "danger", "COMPLETED": "success"
          },

      propertyFilter: {
        creationDateFrom: null,
        creationDateUtil: null,
        assignationDateFrom: null,
        assignationDateUtil: null,
        active: 1,
        ids: []
      },
      dialogs: {
        client: false,
      },
      uploadFile: false,
      buildingUpdated: false,
      //Listeners
      listeners: {
        submit: () => {
        }
      },
    }
  },
  watch: {
    id: function () { // watch it
      this.onLoad()
      this.error = null
    },
  },
  computed: {
    ...mapGetters('translator', ['msg']),
    ...mapGetters('building', ['fileTags', 'allStatuse', 'canOperateBuilding']),
    ...mapGetters('data', ['getUserFullName', 'getUserAcronyms']),
    ...mapGetters('data', ['getAllLanguage', 'getAllBranches', 'getAllCountries', 'getAdminRoleId']),
    ...mapGetters('refinance', ['allStatus', 'allTypes']),

    refinanceType() {
      return this.allTypes.map(status => ({
        value: status.value,
        text: this.msg(status.text)
      }))
    },
    allStatus() {
      return this.allStatuse.map(status => ({
        value: status.value,
        text: status.text.toUpperCase()
      }))
    },
    canSubmit: {
      get() {
        return false
      },
      save(v) {
        this.$emit('update:canSave', v)
      }
    },
    dropzoneOptions() {
      if (this.building == null || this.building.id == null)
        return {};
      return {
        url: window.config.apiUrl + 'building/upload/file/' + this.building.id,
        headers: {'Authorization': `Bearer ${localStorage.Bearer}`},
        createImageThumbnails: false,
        thumbnailWidth: 200,
        thumbnailHeight: 200,
        addRemoveLinks: true,
        maxFilesize: 5000000,
        acceptedFiles: "image/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.tsv,.ppt,.pptx",
      }
    },
    dropzoneImagesOptions() {
      if (this.building == null || this.building.id == null)
        return {};
      return {
        url: window.config.apiUrl + 'building/upload/img',
        headers: {'Authorization': `Bearer ${localStorage.Bearer}`, 'buildingId': this.building.id},
        createImageThumbnails: false,
        thumbnailWidth: 200,
        thumbnailHeight: 200,
        addRemoveLinks: true,
        maxFilesize: 5000000,
        acceptedFiles: 'image/*,video/*,.mp4',
      }
    },
    renterCount: {
      get() {
        return "coming soon"
      }
    },

    ownerCount: {
      get() {
        return "coming soon"
      }
    },
    allBranches() {
      return Object.values(this.getAllBranches)
    },
  },
  created() {
    if (this.$route.params.id == null) {
      return this.error = 'Invalid building id'
    }
    this.id = this.$route.params.id;
    this.onLoad()
  },

  methods: {
    ...mapActions('building', {_getBuilding: 'getBuildingById', _updateBuilding: 'updateBuilding',}),
    ...mapActions('refinance', ['addRefinance']),
    ...mapActions('building', ['updateDocumentFile']),
    ...mapActions("document", ["downloadCustomFile"]),
    ...mapActions('building', ['sendChoosenFilesToMail','addProperty']),
    sendAllFilesByMail() {

      this.sendChoosenFilesToMail({
        buildingId: this.building.id,
        email: this.emailAddress,
        filesId: this.selectedFileIds,
        isBank: false
      });

    },
    onDelete() {
      console.log("onDelete id ")

      let $this = this

      this._updateBuilding(this.building)
          .then(
              (a) => {
                $this.$emit('submitted', {
                  status: true,
                  server: a
                })
                $this.loadBuilding()

              },
              (a) => $this.$emit('submitted', {
                status: false,
                server: a
              })
          ).finally(
          () => {
            $this.onLoad()
            $this.newPropertyId = null;
          }
      )
    },
    uploadImageComplete(file, response) {
      this.loadBuilding()

      console.log('uploadImageComplete', {file, response})
    },
    uploadImageSuccess(file, response) {
      try {
        if (response.data == null || response.data['Building'] == null)
          return;
        this.building.images = response.data['Building']['images'];
      } catch (e) {
        console.error('uploadSuccess error', e)
      }
    },
    resetInfoModal() {
      this.$root.$emit('bv::hide::modal', this.infoModal.id)
    },
    info(value) {
      this.infoModal.value = value;
      this.infoModal.title = 'Ask for a refinance for this building'
      this.$root.$emit('bv::show::modal', this.infoModal.id)
    },
    goToRefinance() {
      if (this.building.refinanceId != null)
        this.$router.push(`/refinance/edit/${this.building.refinanceId}`);
    },
    addClientToBuilding() {

      let $this = this
      if (this.building.clientIds === null)
        this.building.clientIds = [];
      this.building.clientIds.push(this.newClientId)

      console.log("addClientToBuilding im here1")
      this._updateBuilding(this.building)
          .then(
              (a) => {
                $this.$emit('submitted', {
                  status: true,
                  server: a
                })

                $this.clientFilter.ids = $this.building.clientIds;

              },
              (a) => $this.$emit('submitted', {
                status: false,
                server: a
              })
          ).finally(() => {

        $this.newClientId = null;

      })

    },
    addPropertyToBuilding() {

      let $this = this
      if (this.building.propertyIds == null)
        this.building.propertyIds = [];
      this.building.propertyIds.push(this.newPropertyId)
      console.log("addPropertyToBuilding im here1")
      this.addProperty({buildingId:this.building.id,propertyId:this.newPropertyId})
          .then(
              (a) => {
                $this.$emit('submitted', {
                  status: true,
                  server: a
                })
                console.log("addPropertyToBuilding im here2")

                $this.loadBuilding()

              },
              (a) => $this.$emit('submitted', {

                status: false,
                server: a
              })
          ).finally(() => {
        console.log("addPropertyToBuilding im here3")

        $this.newPropertyId = null;
        $this.load()

      })

    },
    convertToEURO(someNumber) {
      if (someNumber == null || isNaN(someNumber))
        return "-";
      return new Intl.NumberFormat("de-AT", {
        style: "currency",
        currency: "EUR",
      }).format(someNumber);
    },
    replaceIfNotNull(data) {
      if (data == null || typeof data !== "string")
        return data;
      return data.replace('€', '').replaceAll(',', '');

    },
    uploadComplete(file, response) {
      let responseData = JSON.parse(file.xhr.responseText);
      let building = responseData.objects["Building"];
      this.building = building;
    },
    uploadSuccess(file, response) {
      try {
        if (response.data == null || response.data['Building'] == null)
          return;
        this.building = response.data['Building'];
      } catch (e) {
        console.error('uploadSuccess error', e)
      }
    },

    submitDocumentFile(index) {
      this.updateDocumentFile({
        id: this.building.id,
        file: this.building.files[index]
      });
    },
    getFileSize(bytes, si = false, dp = 1) {
      const thresh = si ? 1000 : 1024;

      if (Math.abs(bytes) < thresh) {
        return '';
      }

      const units = si
          ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
          : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
      let u = -1;
      const r = 10 ** dp;

      do {
        bytes /= thresh;
        ++u;
      } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);


      return ' (' + bytes.toFixed(dp) + ' ' + units[u] + ')';
    },
    createRefinance() {
      let $this = this;
      this.addRefinance({id: this.building.id, type: this.infoModal.value}).then(ans => {

        $this.building.refinanceId = ans;
        $this.resetInfoModal()
      });
    },
    onDownloadFile(fileId) {
      let $this = this;
      this.downloadCustomFile({fileId: fileId}).then(file => {
        $this.asyncDownloadFile(file)
      })
    },
    onPreviewFile(fileId) {
      let $this = this;
      this.downloadCustomFile({fileId: fileId}).then(file => {

        let url = URL.createObjectURL(file.data);
        console.log("url is: ", url)
        window.open(url, '_blank')
      })
    },
    iconByExt(file) {
      if (file == null) {
        return {fas: true, 'fa-file-alt': true};
      }
      let ext = file.substr(file.lastIndexOf('.') + 1)
      if (ext === 'pdf') {
        return {far: true, 'fa-file-pdf': true}
      }
      if (ext === 'jpg' || ext === 'jpeg' || ext === 'png' || ext === 'gift' || ext === 'bmp') {
        return {far: true, ' fa-file-image': true}
      }
      if (ext === 'doc' || ext === 'docx') {
        return {far: true, ' fa-file-word': true}
      }
      if (ext === 'xls' || ext === 'xlsx') {
        return {far: true, ' fa-file-word': true}
      }
      if (ext === 'zip' || ext === 'rar' || ext === '7zip') {
        return {far: true, ' fa-file-archive': true}
      }
      return {fas: true, 'fa-file-alt': true}
    },
    onClientSelect(client) {
      this.selected = client.id
      this.dialogs.client = true
      this.$root.$emit('bv::hide::popover')
      this.$root.$emit('crm::client::select', client.id)

    },
    onLoad() {
      this.loaded = false

      this.loadBuilding()

    },

    loadBuilding() {
      let $this = this
      console.log("addPropertyToBuilding im here4")

      this._getBuilding($this.id).then((building) => {

        $this.building = BuildingModel(building)
        //Set page as loaded
        if (building.clientIds == null || building.clientIds.length < 1)
          $this.clientFilter.ids = [0];
        else $this.clientFilter.ids = building.clientIds;

        $this.loaded = true
        $this.buildingUpdated = false;
      }, (error) => {
        $this.error = error
      })
    }
    ,

    updateBuilding() {
      let $this = this
      this._updateBuilding(this.building)
          .then(
              (a) => $this.$emit('submitted', {
                status: true,
                server: a
              }),
              (a) => $this.$emit('submitted', {
                status: false,
                server: a
              })
          ).finally(
          () => {
            $this.onLoad()
          }
      )
    },
    validate(field) {
      return this.$v.building[field] != null && this.$v.building[field].$error === true
    },
    submit() {
      if (this.id !== 0) {
        return this.updateBuilding()
      }
      return this.createBuilding()

    },


  },

}
</script>

<style scoped lang="scss">
$eb-radius: 14px;
$eb-radius-lg: 18px;
$eb-border: rgba(60, 60, 70, 0.08);
$eb-border-dark: rgba(255, 255, 255, 0.06);
$eb-primary: #7367f0;

.eb-page {
  min-height: 80vh;
  padding-bottom: 100px;
}

/* ── Hero Header ─────────────────────────────────────────── */
.eb-hero {
  background: linear-gradient(135deg, rgba(115, 103, 240, 0.06) 0%, rgba(255, 159, 67, 0.04) 100%);
  border: 1px solid $eb-border;
  border-radius: $eb-radius-lg;
  padding: 22px 26px 18px 26px;
  margin-bottom: 18px;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: -40%;
    right: -10%;
    width: 280px;
    height: 280px;
    background: radial-gradient(circle, rgba(115, 103, 240, 0.12) 0%, transparent 70%);
    pointer-events: none;
  }
}

.eb-hero__breadcrumb {
  display: flex;
  align-items: center;
  font-size: 0.78rem;
  color: var(--secondary);
  margin-bottom: 8px;
  position: relative;
  z-index: 1;
}

.eb-hero__crumb {
  display: inline-flex;
  align-items: center;
  color: var(--secondary);

  &:hover {
    color: $eb-primary;
  }
}

.eb-hero__crumb-sep {
  margin: 0 8px;
  opacity: 0.4;
}

.eb-hero__crumb-current {
  font-weight: 600;
  color: var(--text);
}

.eb-hero__main {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  position: relative;
  z-index: 1;
}

.eb-hero__icon-wrap {
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  background: linear-gradient(135deg, $eb-primary 0%, #5e50ee 100%);
  color: white;
  box-shadow: 0 8px 22px rgba(115, 103, 240, 0.32);
  flex-shrink: 0;
}

.eb-hero__titles {
  flex: 1;
  min-width: 0;
}

.eb-hero__title {
  font-size: 1.6rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.02em;
  line-height: 1.2;
}

.eb-hero__subtitle {
  font-size: 0.88rem;
  color: var(--secondary);
  margin: 4px 0 0 0;
  font-weight: 500;
  display: flex;
  align-items: center;
}

.eb-hero__meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.eb-hero__status {
  text-transform: capitalize;
  font-weight: 600;
  padding: 6px 12px;
}

.eb-hero__chip {
  display: inline-flex;
  align-items: center;
  background: rgba(115, 103, 240, 0.1);
  color: $eb-primary;
  font-size: 0.78rem;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 999px;
}

.eb-hero__alert {
  border-radius: 12px;
  font-size: 0.85rem;
  padding: 10px 16px;
  display: flex;
  align-items: center;
}

/* ── Tabs ────────────────────────────────────────────────── */
.eb-tabs ::v-deep .eb-tabs__nav-wrap {
  background: var(--card-bg, white);
  border: 1px solid $eb-border;
  border-radius: $eb-radius-lg;
  padding: 6px;
  margin-bottom: 18px;
  position: sticky;
  top: 78px;
  z-index: 10;
  backdrop-filter: blur(8px);
}

.eb-tabs ::v-deep .eb-tabs__nav {
  border: none;
  flex-wrap: wrap;
  gap: 2px;

  .nav-item .nav-link {
    border: none;
    border-radius: 10px;
    padding: 8px 14px;
    color: var(--secondary);
    font-weight: 600;
    font-size: 0.85rem;
    transition: all 0.18s ease;
    margin-right: 0;
  }

  .nav-item .nav-link:hover {
    background: rgba(115, 103, 240, 0.08);
    color: $eb-primary;
  }

  .nav-item .nav-link.active {
    background: linear-gradient(135deg, $eb-primary 0%, #5e50ee 100%);
    color: white;
    box-shadow: 0 6px 16px rgba(115, 103, 240, 0.32);

    .feather { color: white; }
  }
}

.eb-tab-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.eb-tabs ::v-deep .eb-tabs__content {
  margin-top: 0;
}

/* ── Section card ────────────────────────────────────────── */
.eb-section {
  background: var(--card-bg, white);
  border: 1px solid $eb-border;
  border-radius: $eb-radius-lg;
  margin-bottom: 16px;
  overflow: hidden;
  transition: box-shadow 0.18s ease;

  &:hover {
    box-shadow: 0 8px 28px rgba(34, 41, 47, 0.06);
  }
}

.eb-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid $eb-border;
  gap: 12px;
  flex-wrap: wrap;
}

.eb-section__title {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;

  h4 {
    font-size: 0.95rem;
    font-weight: 700;
    margin: 0;
    letter-spacing: -0.01em;
  }
}

.eb-section__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 9px;
  background: rgba(115, 103, 240, 0.1);
  color: $eb-primary;
  flex-shrink: 0;

  i { font-size: 14px; line-height: 1; }
  ::v-deep svg { color: $eb-primary; }
}

.eb-section__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.eb-section__body {
  padding: 18px;

  &.p-0 { padding: 0; }
}

.eb-icon-btn {
  border-radius: 10px !important;
  padding: 8px 10px !important;
}

/* ── Add property bar (inside section header) ───────── */
.eb-add-property {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  flex: 1;
  justify-content: flex-end;
  max-width: 600px;
}

.eb-add-property__picker {
  flex: 1;
  min-width: 220px;
}

.eb-add-property__btn {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

/* ── Files area ──────────────────────────────────────────── */
.eb-files-alert {
  font-size: 0.82rem;
  border-radius: 10px;
  margin-bottom: 0;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.eb-dropzone-wrap ::v-deep .dropzone {
  border-radius: 12px;
  border: 2px dashed rgba(115, 103, 240, 0.4);
  background: rgba(115, 103, 240, 0.04);
}

.eb-files-table {
  border: 1px solid $eb-border;
  border-radius: 12px;
  overflow: hidden;
}

.eb-files-thead th {
  background: rgba(115, 103, 240, 0.08);
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 700;
  color: $eb-primary;
  padding: 10px 12px;
  border: none;
}

.eb-files-row td {
  padding: 12px;
  vertical-align: middle;
  border-top: 1px solid $eb-border;
}

.eb-file-icon {
  font-size: 26px;
  color: $eb-primary;
  flex-shrink: 0;
}

.eb-file-name {
  font-weight: 600;
  font-size: 0.86rem;
  word-break: break-all;
}

.eb-action-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: rgba(115, 103, 240, 0.08);
  color: $eb-primary;
  transition: all 0.15s ease;

  &:hover {
    background: $eb-primary;
    color: white;
    transform: translateY(-1px);
  }
}

/* ── Empty state ─────────────────────────────────────────── */
.eb-empty {
  text-align: center;
  padding: 32px 16px;
  color: var(--secondary);
  font-size: 0.88rem;
}

/* ── Tracker tab ─────────────────────────────────────────── */
.eb-tracker-row {
  display: flex;
  align-items: flex-end;
  gap: 14px;
  flex-wrap: wrap;
}

.eb-tracker-row__field {
  flex: 1;
  min-width: 280px;
}

.eb-tracker-row__toggle {
  align-self: flex-end;
}

.eb-timeline-wrap {
  background: rgba(115, 103, 240, 0.04);
  border-radius: 12px;
  padding: 16px;
}

/* ── Floating save bar ───────────────────────────────────── */
.eb-save-bar {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 100;
}

.eb-save-bar__inner {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--card-bg, white);
  border: 1px solid $eb-border;
  border-radius: 999px;
  padding: 8px 12px 8px 18px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.18), 0 4px 12px rgba(0, 0, 0, 0.08);
}

.eb-save-bar__hint {
  font-size: 0.8rem;
  font-weight: 600;
  color: $eb-primary;
  margin-right: 6px;
  display: inline-flex;
  align-items: center;
}

.eb-fade-up-enter-active,
.eb-fade-up-leave-active {
  transition: all 0.28s cubic-bezier(0.4, 0, 0.2, 1);
}

.eb-fade-up-enter,
.eb-fade-up-leave-to {
  transform: translate(-50%, 30px);
  opacity: 0;
}

/* ── Loading ─────────────────────────────────────────────── */
.eb-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 600px;
}

/* ── Dropzone reset ──────────────────────────────────────── */
.eb-page ::v-deep .dz-preview,
.eb-page ::v-deep .dz-file-preview {
  display: none;
}

/* ── Dark mode ───────────────────────────────────────────── */
.dark-layout {
  .eb-hero {
    background: linear-gradient(135deg, rgba(115, 103, 240, 0.12) 0%, rgba(255, 159, 67, 0.06) 100%);
    border-color: $eb-border-dark;
  }

  .eb-tabs ::v-deep .eb-tabs__nav-wrap,
  .eb-section,
  .eb-save-bar__inner {
    background: #283046;
    border-color: $eb-border-dark;
  }

  .eb-section:hover {
    box-shadow: 0 8px 28px rgba(0, 0, 0, 0.32);
  }

  .eb-files-table,
  .eb-files-row td,
  .eb-section__head {
    border-color: $eb-border-dark;
  }
}

/* ── Responsive ──────────────────────────────────────────── */
@media (max-width: 768px) {
  .eb-hero {
    padding: 16px 18px;
  }
  .eb-hero__title {
    font-size: 1.3rem;
  }
  .eb-hero__main {
    flex-wrap: wrap;
  }
  .eb-section__head {
    flex-direction: column;
    align-items: flex-start;
  }
  .eb-add-property {
    width: 100%;
    justify-content: stretch;
  }
}

/* Popover keep — used by image upload trigger */
::v-deep .popover-body {
  width: 250px;
}
</style>
